import { useEffect, useRef, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";

/** Native <dialog> wrapper — handles open/close + ESC, dim backdrop, click-outside closes. */
export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!el.open) el.showModal();
    return () => { if (el.open) el.close(); };
  }, []);

  // Native dialog fires `close` when ESC is pressed or close() is called — funnel both into onClose.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => onClose();
    el.addEventListener("close", handler);
    return () => el.removeEventListener("close", handler);
  }, [onClose]);

  // Click on the backdrop (i.e. anywhere outside the inner content) closes the dialog.
  function handleClick(e: ReactMouseEvent<HTMLDialogElement>) {
    if (e.target === ref.current) ref.current?.close();
  }

  return (
    <dialog ref={ref} className="modal" onClick={handleClick}>
      <div className="modal-head">
        <span className="title">{title}</span>
        <button className="btn btn-ghost btn-sm" onClick={() => ref.current?.close()}>Close</button>
      </div>
      <div className="modal-body">{children}</div>
    </dialog>
  );
}
